<template>
  <v-col class="docs-landing">
    <v-row>
      <v-col>
        <docs-hero-banner
          eyebrow="Documentation Hub"
          title="Welcome to iAdOps"
          description="Your central hub for ad operations management, delivery monitoring, and team productivity."
          :meta-items="heroMetaItems"
        >
          <template v-slot:actions>
            <docs-callout
              title="New help model"
              color="info"
              :icon="['fas', 'circle-question']"
            >
              Header Help and navigation shortcuts now point into this docs hub,
              release summary, and route-specific guides so support content is
              easier to reach from any screen.
            </docs-callout>
          </template>
        </docs-hero-banner>
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col cols="12">
        <docs-section-card
          title="Quick Start"
          description="Jump into the most common routes from one place."
          :icon="['fas', 'rocket']"
        >
          <v-row>
            <v-col
              v-for="card in quickStartCards"
              :key="card.title"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card
                class="quick-start-card pa-4"
                :to="card.route"
                hover
              >
                <v-row
                  align="center"
                  no-gutters
                >
                  <v-col class="col-auto mr-3">
                    <v-avatar
                      :color="card.color"
                      size="40"
                    >
                      <font-awesome-icon
                        :icon="card.icon"
                        class="white--text"
                        size="sm"
                      />
                    </v-avatar>
                  </v-col>
                  <v-col>
                    <div class="subtitle-2 font-weight-bold">
                      {{ card.title }}
                    </div>
                    <div class="caption coolgray--text">
                      {{ card.description }}
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </docs-section-card>
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col cols="12">
        <docs-section-card
          title="Core Features"
          description="Use this section when you need a quick map of what the product covers."
          :icon="['fas', 'layer-group']"
        >
          <v-row>
            <v-col
              v-for="feature in coreFeatures"
              :key="feature.title"
              cols="12"
              md="6"
            >
              <v-card
                class="feature-card pa-4 mb-3"
                outlined
              >
                <div class="d-flex align-center mb-2">
                  <font-awesome-icon
                    :icon="feature.icon"
                    :class="`${feature.color}--text mr-2`"
                  />
                  <span class="subtitle-2 font-weight-bold">
                    {{ feature.title }}
                  </span>
                </div>
                <p class="body-2 coolgray--text mb-0">
                  {{ feature.description }}
                </p>
              </v-card>
            </v-col>
          </v-row>
        </docs-section-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <docs-section-card
          title="Getting Help"
          description="Choose the help path that matches the kind of answer you need."
          :icon="['fas', 'life-ring']"
        >
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <div class="d-flex align-center mb-2">
                <font-awesome-icon
                  :icon="['fas', 'book']"
                  class="primary--text mr-2"
                />
                <span class="subtitle-2 font-weight-bold">
                  Documentation
                </span>
              </div>
              <p class="body-2 coolgray--text mb-0">
                Browse the <router-link to="/docs/features">
                  Features
                </router-link>
                and <router-link to="/docs/filters">
                  Filters
                </router-link> guides
                to learn how to use the app.
              </p>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <div class="d-flex align-center mb-2">
                <font-awesome-icon
                  :icon="['fas', 'rocket']"
                  class="success--text mr-2"
                />
                <span class="subtitle-2 font-weight-bold">
                  Release Notes
                </span>
              </div>
              <p class="body-2 coolgray--text mb-0">
                Check the <router-link to="/docs/release-notes">
                  Release Notes
                </router-link>
                for the latest updates and improvements.
              </p>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <div class="d-flex align-center mb-2">
                <font-awesome-icon
                  :icon="['fas', 'headset']"
                  class="warning--text mr-2"
                />
                <span class="subtitle-2 font-weight-bold">
                  Support
                </span>
              </div>
              <p class="body-2 coolgray--text mb-0">
                Contact your account manager or system administrator
                for assistance with your account.
              </p>
            </v-col>
          </v-row>
        </docs-section-card>
      </v-col>
    </v-row>

    <!-- Version Info -->
    <v-row class="mt-6">
      <v-col>
        <p class="caption coolgray--text text-center mb-0">
          iAdOps version {{ version }}
        </p>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapState } = createNamespacedHelpers('layout')
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'DocsLanding',
  components: {
    DocsCallout: () => import('@/docs/components/DocsCallout.vue'),
    DocsHeroBanner: () => import('@/docs/components/DocsHeroBanner.vue'),
    DocsSectionCard: () => import('@/docs/components/DocsSectionCard.vue'),
  },
  props: {
    showInternalContent: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      version: (state) => state.version,
    }),
    ...mapAuthGetters(['isInternalUser']),

    heroMetaItems() {
      return [
        { label: 'Audience: All signed-in users', icon: ['fas', 'users'] },
        { label: 'Use Help from header or sidebar', icon: ['fas', 'circle-question'] },
        { label: `Version: ${this.version}`, icon: ['fas', 'tag'] },
      ]
    },

    quickStartCards() {
      const cards = [
        {
          title: 'Pacing',
          description: 'Monitor campaign delivery and pacing status',
          icon: ['fas', 'chart-line'],
          color: 'primary',
          route: '/reports/delivery/pacing',
          internalOnly: false,
        },
        {
          title: 'Time Entry',
          description: 'Log and manage your time entries',
          icon: ['fas', 'clock'],
          color: 'success',
          route: '/internal/time',
          internalOnly: true,
        },
        {
          title: 'Projects',
          description: 'View and manage active projects',
          icon: ['fas', 'folder-open'],
          color: 'warning',
          route: '/internal/projects',
          internalOnly: true,
        },
      ]

      return this.showInternalContent
        ? cards
        : cards.filter((card) => !card.internalOnly)
    },

    coreFeatures() {
      const features = [
        {
          title: 'Delivery Monitoring',
          description: 'Track pacing and discrepancy reports across your ad networks. Monitor campaign health with real-time delivery data from Google Ad Manager.',
          icon: ['fas', 'chart-bar'],
          color: 'primary',
          internalOnly: false,
        },
        {
          title: 'Time Management',
          description: 'Log time entries against projects and clients. View month-to-date summaries and billable hours with calendar and table layouts.',
          icon: ['fas', 'clock'],
          color: 'success',
          internalOnly: true,
        },
        {
          title: 'Organization Management',
          description: 'Manage clients, teams, and organizational structures. Track billing codes, enterprise configurations, and publisher relationships.',
          icon: ['fas', 'building'],
          color: 'info',
          internalOnly: true,
        },
        {
          title: 'Project Tracking',
          description: 'Create and manage projects with time tracking integration. Assign teams, set budgets, and monitor project progress.',
          icon: ['fas', 'tasks'],
          color: 'warning',
          internalOnly: true,
        },
      ]

      return this.showInternalContent
        ? features
        : features.filter((feature) => !feature.internalOnly)
    },
  },
}
</script>

<style lang="scss" scoped>
.docs-landing {
  max-width: 900px;
}

.quick-start-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }
}

.feature-card {
  height: 100%;
}
</style>
